package br.com.vvrefeicoes.portal.feature.notifications

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.core.util.image.isImageUploadSupported
import br.com.vvrefeicoes.portal.domain.model.Announcement
import br.com.vvrefeicoes.portal.domain.model.AnnouncementAudience
import br.com.vvrefeicoes.portal.domain.model.AnnouncementDraft
import br.com.vvrefeicoes.portal.domain.model.AnnouncementTarget
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.NotificationPriority
import br.com.vvrefeicoes.portal.domain.model.UserProfile
import br.com.vvrefeicoes.portal.domain.repository.AnnouncementRepository
import br.com.vvrefeicoes.portal.domain.repository.CompanyRepository
import br.com.vvrefeicoes.portal.domain.repository.UserCandidate
import br.com.vvrefeicoes.portal.domain.repository.UserRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

enum class AnnouncementImageMode { RESOURCES, UPLOAD }

class AnnouncementsManagementController(
    private val repository: AnnouncementRepository,
    private val userRepository: UserRepository,
    private val companyRepository: CompanyRepository,
    private val scope: CoroutineScope,
) {
    private var initialized = false
    private var referencesLoaded = false
    private var totalPages = 1
    private var loadJob: Job? = null

    var loading by mutableStateOf(true)
        private set

    var busy by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var announcements by mutableStateOf<List<Announcement>>(emptyList())
        private set

    var query by mutableStateOf("")

    var period by mutableStateOf(PeriodSelection.current(SystemDateTimeProvider().currentPeriod()).copy(mode = PeriodMode.TOTAL))
        private set

    var page by mutableStateOf(0)
        private set

    var profiles by mutableStateOf<List<UserProfile>>(emptyList())
        private set

    var companies by mutableStateOf<List<Company>>(emptyList())
        private set

    var users by mutableStateOf<List<UserCandidate>>(emptyList())
        private set

    var formOpen by mutableStateOf(false)
        private set

    var editingId by mutableStateOf<String?>(null)
        private set

    var formTitle by mutableStateOf("")
    var formBody by mutableStateOf("")
    var formPriority by mutableStateOf(NotificationPriority.MEDIUM)
    var formImageUrl by mutableStateOf("")
    var imageMode by mutableStateOf(defaultImageMode())
    var uploading by mutableStateOf(false)
        private set

    var viewing by mutableStateOf<Announcement?>(null)
        private set

    var showPublishSuccess by mutableStateOf(false)
        private set

    var deleteTarget by mutableStateOf<Announcement?>(null)
        private set

    /** null = Todos. */
    var audienceMode by mutableStateOf<AnnouncementAudience?>(null)
        private set

    var selectedRoles by mutableStateOf<Set<String>>(emptySet())
        private set

    var selectedUnits by mutableStateOf<Set<String>>(emptySet())
        private set

    var selectedUsers by mutableStateOf<Set<String>>(emptySet())
        private set

    val canSubmit: Boolean
        get() = formTitle.isNotBlank() && !busy

    val pageCount: Int
        get() = maxOf(1, totalPages)

    fun init() {
        if (initialized) return
        initialized = true
        scope.launch { loadReferences() }
        requestPage()
    }

    fun retry() {
        requestPage()
    }

    private suspend fun loadReferences() {
        if (referencesLoaded) return
        referencesLoaded = true
        profiles = runCatching { userRepository.loadProfiles() }.getOrDefault(emptyList())
        companies = runCatching { companyRepository.loadCompanies() }.getOrDefault(emptyList())
        users = runCatching { userRepository.loadUserCandidates() }.getOrDefault(emptyList())
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
        requestPage(SEARCH_DEBOUNCE_MS)
    }

    fun applyPeriod(selection: PeriodSelection) {
        period = selection
        page = 0
        requestPage()
    }

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        requestPage()
    }

    private fun requestPage(delayMillis: Long = 0) {
        loadJob?.cancel()
        loadJob =
            scope.launch {
                if (delayMillis > 0) delay(delayMillis)
                loading = true
                errorMessage = null
                try {
                    val result = repository.loadAnnouncementsPage(page, PAGE_SIZE, query, period)
                    announcements = result.content
                    totalPages = result.totalPages
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    errorMessage = throwable.message ?: "Não foi possível carregar os comunicados."
                } finally {
                    loading = false
                }
            }
    }

    fun openCreate() {
        editingId = null
        formTitle = ""
        formBody = ""
        formPriority = NotificationPriority.MEDIUM
        formImageUrl = ""
        imageMode = defaultImageMode()
        audienceMode = null
        selectedRoles = emptySet()
        selectedUnits = emptySet()
        selectedUsers = emptySet()
        formOpen = true
    }

    /** Só rascunhos podem ser editados; comunicados publicados são apenas visualizados. */
    fun openEdit(announcement: Announcement) {
        if (announcement.active) return
        editingId = announcement.id
        formTitle = announcement.title
        formBody = announcement.body
        formPriority = announcement.priority
        formImageUrl = announcement.imageUrl.orEmpty()
        imageMode =
            if (announcement.imageUrl?.startsWith("resource:") == true) {
                AnnouncementImageMode.RESOURCES
            } else {
                defaultImageMode()
            }
        selectedRoles =
            announcement.targets
                .filter { it.type == AnnouncementAudience.ROLE }
                .map { it.ref }
                .toSet()
        selectedUnits =
            announcement.targets
                .filter { it.type == AnnouncementAudience.UNIT }
                .map { it.ref }
                .toSet()
        selectedUsers =
            announcement.targets
                .filter { it.type == AnnouncementAudience.USER }
                .map { it.ref }
                .toSet()
        audienceMode =
            when {
                selectedRoles.isNotEmpty() -> AnnouncementAudience.ROLE
                selectedUnits.isNotEmpty() -> AnnouncementAudience.UNIT
                selectedUsers.isNotEmpty() -> AnnouncementAudience.USER
                else -> null
            }
        formOpen = true
    }

    fun dismissForm() {
        formOpen = false
    }

    fun openView(announcement: Announcement) {
        viewing = announcement
    }

    fun closeView() {
        viewing = null
    }

    fun selectAudience(mode: AnnouncementAudience?) {
        audienceMode = mode
        selectedRoles = emptySet()
        selectedUnits = emptySet()
        selectedUsers = emptySet()
    }

    fun toggleRole(id: String) {
        selectedRoles = selectedRoles.toggle(id)
    }

    fun toggleUnit(id: String) {
        selectedUnits = selectedUnits.toggle(id)
    }

    fun toggleUser(id: String) {
        selectedUsers = selectedUsers.toggle(id)
    }

    fun setImageResource(key: String) {
        formImageUrl = if (formImageUrl == "resource:$key") "" else "resource:$key"
    }

    /** Troca o modo de imagem e reseta a escolha anterior. */
    fun selectImageMode(mode: AnnouncementImageMode) {
        if (mode == imageMode) return
        imageMode = mode
        formImageUrl = ""
    }

    fun clearImage() {
        formImageUrl = ""
    }

    fun uploadImage(
        bytes: ByteArray,
        contentType: String,
    ) {
        uploading = true
        scope.launch {
            try {
                formImageUrl = repository.uploadImage(bytes, contentType)
                imageMode = AnnouncementImageMode.UPLOAD
                errorMessage = null
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível enviar a imagem."
            } finally {
                uploading = false
            }
        }
    }

    /** Valida título e mensagem antes de abrir a prévia da publicação. */
    fun validate(bodyHtml: String): Boolean {
        if (formTitle.isBlank() || bodyHtml.isBlank()) {
            errorMessage = "Preencha o título e a mensagem do comunicado."
            return false
        }
        return true
    }

    fun submit(
        bodyHtml: String,
        publish: Boolean,
    ) {
        val html = bodyHtml.trim()
        if (formTitle.isBlank() || html.isBlank() || busy) {
            errorMessage = "Preencha o título e a mensagem do comunicado."
            return
        }
        val draft =
            AnnouncementDraft(
                title = formTitle.trim(),
                body = html,
                priority = formPriority,
                imageUrl = formImageUrl.trim().ifEmpty { null },
                active = publish,
                targets = selectedTargets(),
            )
        busy = true
        scope.launch {
            try {
                val id = editingId
                if (id == null) repository.create(draft) else repository.update(id, draft)
                formOpen = false
                errorMessage = null
                if (publish) showPublishSuccess = true
                requestPage()
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível salvar o comunicado."
            } finally {
                busy = false
            }
        }
    }

    fun dismissPublishSuccess() {
        showPublishSuccess = false
    }

    fun delete(announcement: Announcement) {
        if (announcement.active) return
        scope.launch {
            try {
                repository.delete(announcement.id)
                announcements = announcements.filterNot { it.id == announcement.id }
                errorMessage = null
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível excluir o comunicado."
            }
        }
    }

    fun requestDelete(announcement: Announcement) {
        if (announcement.active) return
        deleteTarget = announcement
    }

    fun dismissDelete() {
        deleteTarget = null
    }

    fun confirmDelete() {
        val target = deleteTarget ?: return
        deleteTarget = null
        delete(target)
    }

    /** Nome amigável dos destinatários de um comunicado. */
    fun audienceNames(announcement: Announcement): List<String> {
        val targets = announcement.targets
        if (targets.isEmpty()) return emptyList()
        return targets.mapNotNull { target ->
            when (target.type) {
                AnnouncementAudience.ROLE -> profiles.firstOrNull { it.id == target.ref }?.name
                AnnouncementAudience.UNIT -> companies.firstOrNull { it.id == target.ref }?.name
                AnnouncementAudience.USER -> users.firstOrNull { it.id == target.ref }?.name
            }
        }
    }

    private fun selectedTargets(): List<AnnouncementTarget> =
        when (audienceMode) {
            null -> emptyList()
            AnnouncementAudience.ROLE -> selectedRoles.map { AnnouncementTarget(AnnouncementAudience.ROLE, it) }
            AnnouncementAudience.UNIT -> selectedUnits.map { AnnouncementTarget(AnnouncementAudience.UNIT, it) }
            AnnouncementAudience.USER -> selectedUsers.map { AnnouncementTarget(AnnouncementAudience.USER, it) }
        }

    private fun Set<String>.toggle(id: String): Set<String> = if (id in this) this - id else this + id

    private companion object {
        const val PAGE_SIZE = 20
        const val SEARCH_DEBOUNCE_MS = 300L

        fun defaultImageMode(): AnnouncementImageMode = if (isImageUploadSupported()) AnnouncementImageMode.UPLOAD else AnnouncementImageMode.RESOURCES
    }
}

@Composable
fun rememberAnnouncementsManagementController(): AnnouncementsManagementController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        AnnouncementsManagementController(
            repository = appGraph.announcementRepository,
            userRepository = appGraph.userRepository,
            companyRepository = appGraph.companyRepository,
            scope = scope,
        )
    }
}
